// Dashboard (8 Oct 2026): the superuser's third launcher card, at /?tool=dashboard.
// Who has a login, which projects each one sees, and invite / reset links to send yourself.
// Every call is /api/admin/*, which the backend restricts to superusers - this page hiding
// itself from others is navigation only. Clients, project->client assignment and backups
// stay in the Manage panel, opened from here.
import {
  api,
  type AdminClient,
  type AdminInvite,
  type AdminUser,
  type IssuedInvite,
  type Project,
} from "../api";
import { openAdminPanel } from "./adminPanel";
import { toast } from "./toast";
import { $user } from "../store";

interface Data {
  clients: AdminClient[];
  users: AdminUser[];
  projects: Project[];
  invites: AdminInvite[];
}

const esc = (s: unknown): string =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

function ago(iso: string | null): string {
  if (!iso) return "";
  const s = (Date.now() - Date.parse(iso)) / 1000;
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400 * 2) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
}

function until(iso: string | null): string {
  if (!iso) return "";
  const d = (Date.parse(iso) - Date.now()) / 86400000;
  return d < 1 ? `${Math.max(1, Math.round(d * 24))} h left` : `${Math.round(d)} days left`;
}

export async function mountDashboard(): Promise<void> {
  document.querySelector(".dash-overlay")?.remove();
  const root = document.createElement("div");
  root.className = "dash-overlay";
  root.innerHTML = `
    <header class="dash-top">
      <div class="dash-brand">GEAAR <span>Dashboard</span></div>
      <nav class="dash-nav">
        <a class="admin-mini" href="/?tool=review-station">Review Station</a>
        <button class="admin-mini" data-act="manage" type="button">Clients, projects &amp; backups</button>
        <button class="admin-mini" data-act="logout" type="button">Sign out</button>
      </nav>
    </header>
    <main class="dash-main"><div class="admin-msg">Loading…</div></main>`;
  document.body.append(root);
  const main = root.querySelector<HTMLElement>(".dash-main")!;
  root.querySelector('[data-act="manage"]')!.addEventListener("click", () => void openAdminPanel());
  root.querySelector('[data-act="logout"]')!.addEventListener("click", async () => {
    await api.logout().catch(() => undefined);
    location.href = "/";
  });
  await render(main);
}

async function load(): Promise<Data> {
  const [clients, users, projects, invites] = await Promise.all([
    api.listClients().then((r) => r.clients),
    api.listUsers().then((r) => r.users),
    api.listProjects(),
    api.listInvites().then((r) => r.invites),
  ]);
  return { clients, users, projects, invites };
}

async function render(main: HTMLElement, issued?: IssuedInvite): Promise<void> {
  let d: Data;
  try {
    d = await load();
  } catch (e) {
    main.innerHTML = `<div class="admin-msg err">Couldn't load: ${esc((e as Error).message)}</div>`;
    return;
  }
  const refresh = (next?: IssuedInvite) => void render(main, next);
  const pending = d.invites.filter((i) => i.status === "pending");
  main.innerHTML = `
    <div class="dash-stats">
      <div><b>${d.users.filter((u) => u.active && !u.is_superuser).length}</b><span>client logins</span></div>
      <div><b>${d.clients.filter((c) => !c.is_internal).length}</b><span>clients</span></div>
      <div><b>${d.projects.length}</b><span>projects</span></div>
      <div><b>${pending.length}</b><span>open invites</span></div>
    </div>
    <div class="dash-grid">
      <section class="dash-card dash-invite"></section>
      <section class="dash-card dash-users"></section>
    </div>
    <section class="dash-card dash-invites"></section>`;
  inviteCard(main.querySelector(".dash-invite")!, d, refresh, issued);
  usersCard(main.querySelector(".dash-users")!, d, refresh);
  invitesCard(main.querySelector(".dash-invites")!, d, refresh);
}

const projectsOf = (d: Data, clientId: number | null) => d.projects.filter((p) => p.client_id === clientId);

/** "Sees all 3 projects: A, B, C" / "Sees 2 of 3: Herries, Karara" */
function seesText(d: Data, clientId: number | null, scope: string, ids: number[], superuser = false): string {
  if (superuser) return "Sees everything (Viewport)";
  const own = projectsOf(d, clientId);
  if (scope !== "selected") {
    if (!own.length) return "No projects yet (will see the client's as they're added)";
    return own.length === 1 ? `Sees ${own[0].name}` : `Sees all ${own.length}: ${own.map((p) => p.name).join(", ")}`;
  }
  const names = own.filter((p) => ids.includes(p.id)).map((p) => p.name);
  return names.length ? `Sees ${names.length} of ${own.length}: ${names.join(", ")}` : "Sees nothing (no projects ticked)";
}

/** Visibility picker: whole client, or only the ticked projects. */
function scopePicker(d: Data, clientId: number | null, scope: string, ids: number[]): {
  el: HTMLElement;
  value: () => { project_scope: "client" | "selected"; project_ids: number[] };
  setClient: (cid: number | null) => void;
} {
  const el = document.createElement("div");
  el.className = "dash-scope";
  const name = `scope-${Math.random().toString(36).slice(2)}`;
  let cid = clientId;
  const draw = () => {
    const own = projectsOf(d, cid);
    el.innerHTML = `
      <label class="admin-check"><input type="radio" name="${name}" value="client" ${scope !== "selected" ? "checked" : ""}/>
        All of the client's projects <span class="dash-dim">(${own.length}; new ones appear automatically)</span></label>
      <label class="admin-check"><input type="radio" name="${name}" value="selected" ${scope === "selected" ? "checked" : ""}/>
        Only these:</label>
      <div class="dash-projects">${
        own.length
          ? own.map((p) => `<label class="admin-check"><input type="checkbox" value="${p.id}" ${ids.includes(p.id) ? "checked" : ""}/>${esc(p.name)}</label>`).join("")
          : `<span class="dash-dim">This client has no projects yet.</span>`
      }</div>`;
    const sync = () => {
      const selected = (el.querySelector(`input[name="${name}"]:checked`) as HTMLInputElement).value === "selected";
      el.querySelector(".dash-projects")!.classList.toggle("off", !selected);
    };
    el.querySelectorAll(`input[name="${name}"]`).forEach((r) => r.addEventListener("change", () => {
      scope = (r as HTMLInputElement).value;
      sync();
    }));
    el.querySelectorAll<HTMLInputElement>(".dash-projects input").forEach((c) => c.addEventListener("change", () => {
      ids = [...el.querySelectorAll<HTMLInputElement>(".dash-projects input:checked")].map((x) => Number(x.value));
    }));
    sync();
  };
  draw();
  return {
    el,
    value: () => ({
      project_scope: scope === "selected" ? "selected" : "client",
      project_ids: scope === "selected" ? ids : [],
    }),
    setClient: (next) => {
      cid = next;
      ids = [];
      draw();
    },
  };
}

// --- Invite someone ---
function inviteCard(card: HTMLElement, d: Data, refresh: (i?: IssuedInvite) => void, issued?: IssuedInvite): void {
  const clients = d.clients.filter((c) => c.active);
  const firstClient = clients.find((c) => !c.is_internal) ?? clients[0];
  card.innerHTML = `
    <h2>Invite someone</h2>
    <p class="dash-dim">Makes a one-time link. You send it; they choose their own username and password.</p>
    <div class="dash-form">
      <input class="admin-input" data-f="name" placeholder="Name (e.g. Ingrid Smith)" />
      <input class="admin-input" data-f="email" type="email" placeholder="Email (for the Email button)" />
      <select class="admin-input" data-f="client">${clients
        .map((c) => `<option value="${c.id}" ${c.id === firstClient?.id ? "selected" : ""}>${esc(c.name)}${c.is_internal ? " (internal)" : ""}</option>`)
        .join("")}</select>
      <select class="admin-input" data-f="role">
        <option value="viewer">Reviewer (comment + sign off)</option>
        <option value="admin">Client admin</option>
      </select>
      <select class="admin-input" data-f="days">
        <option value="7">Link lasts 7 days</option><option value="14">14 days</option><option value="30">30 days</option><option value="2">2 days</option>
      </select>
    </div>
    <div class="dash-label">Can see</div>`;
  const get = (f: string) => (card.querySelector(`[data-f="${f}"]`) as HTMLInputElement | HTMLSelectElement).value;
  const picker = scopePicker(d, firstClient?.id ?? null, "client", []);
  card.append(picker.el);
  card.querySelector('[data-f="client"]')!.addEventListener("change", () => picker.setClient(Number(get("client"))));
  const mvInvite = modelViewerTick(false);
  card.append(mvInvite.el);

  const go = document.createElement("button");
  go.className = "login-btn dash-go";
  go.type = "button";
  go.textContent = "Create invite link";
  go.addEventListener("click", async () => {
    const v = picker.value();
    if (v.project_scope === "selected" && !v.project_ids.length) {
      toast("Tick at least one project, or choose all of the client's", "warn");
      return;
    }
    go.disabled = true;
    try {
      const inv = await api.createInvite({
        client_id: Number(get("client")),
        display_name: get("name").trim() || undefined,
        email: get("email").trim() || undefined,
        role: get("role"),
        days: Number(get("days")),
        model_viewer: mvInvite.value(),
        ...v,
      });
      refresh(inv);
    } catch (e) {
      toast((e as Error).message, "warn", 6000);
      go.disabled = false;
    }
  });
  card.append(go);
  if (issued) card.append(issuedBox(issued));
}

/** The link, shown once: copy it, or open a ready-written email in the user's own mail. */
function issuedBox(inv: IssuedInvite): HTMLElement {
  const url = `${location.origin}${inv.path}`;
  const box = document.createElement("div");
  box.className = "dash-issued";
  const who = inv.kind === "reset" ? `${inv.username ?? inv.display_name}'s password reset` : `${inv.display_name ?? "Invite"} (${inv.client_name})`;
  box.innerHTML = `
    <div class="dash-issued-head">Link for ${esc(who)} <span class="dash-dim">· ${esc(until(inv.expires_at))} · shown once</span></div>
    <div class="dash-issued-row">
      <input class="admin-input dash-link" readonly value="${esc(url)}" />
      <button class="admin-mini" data-act="copy" type="button">Copy link</button>
    </div>
    <div class="dash-issued-row dash-issued-send">
      <a class="admin-mini dash-gmail" data-act="gmail" href="#" target="_blank" rel="noopener">Open in Gmail</a>
      <button class="admin-mini" data-act="copymsg" type="button">Copy message</button>
      <a class="admin-mini" data-act="mail" href="#" title="Needs a mail app set for email links in Windows">Other mail app</a>
    </div>`;
  const input = box.querySelector<HTMLInputElement>(".dash-link")!;
  input.addEventListener("focus", () => input.select());
  box.querySelector('[data-act="copy"]')!.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast("Link copied", "ok");
    } catch {
      input.select();
      toast("Press Ctrl+C to copy", "info");
    }
  });
  const greeting = (inv.display_name ?? "").split(" ")[0] || "there";
  const expires = inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : "";
  const subject = inv.kind === "reset" ? "Reset your GEAAR password" : "Your access to GEAAR";
  const body = inv.kind === "reset"
    ? `Hi ${greeting},\n\nUse this link to set a new password for GEAAR (username: ${inv.username}):\n\n${url}\n\nIt works once and expires on ${expires}.\n`
    : `Hi ${greeting},\n\nYou've been given access to ${inv.client_name ?? "your"} project${
        inv.project_scope === "selected" && inv.project_ids.length === 1 ? "" : "s"
      } on GEAAR, our 3D/2D review site.\n\nOpen this link to choose your username and password:\n\n${url}\n\nIt works once and expires on ${expires}. After that, sign in at ${location.origin}\n`;
  const to = encodeURIComponent(inv.email ?? "");
  // A mailto: link does nothing on a PC with no mail app set for it (Chad's, 8 Oct), so Gmail's
  // own compose page is the main button; the mailto stays for anyone with a desktop mail app.
  box.querySelector<HTMLAnchorElement>('[data-act="gmail"]')!.href =
    `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  box.querySelector<HTMLAnchorElement>('[data-act="mail"]')!.href =
    `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  box.querySelector('[data-act="copymsg"]')!.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      toast("Message copied - paste it into any email", "ok");
    } catch {
      toast("Couldn't copy - use Copy link instead", "warn");
    }
  });
  return box;
}

// --- Users ---
function usersCard(card: HTMLElement, d: Data, refresh: (i?: IssuedInvite) => void): void {
  card.innerHTML = `<h2>Users</h2>`;
  const byClient = [...d.clients].sort((a, b) => Number(a.is_internal) - Number(b.is_internal) || a.name.localeCompare(b.name));
  for (const c of byClient) {
    const users = d.users.filter((u) => u.client_id === c.id);
    if (!users.length) continue;
    const group = document.createElement("div");
    group.className = "dash-group";
    group.innerHTML = `<div class="dash-group-head">${esc(c.name)} <span class="dash-dim">${users.length} login${users.length === 1 ? "" : "s"} · ${projectsOf(d, c.id).length} project${projectsOf(d, c.id).length === 1 ? "" : "s"}</span></div>`;
    for (const u of users) group.append(userRow(d, u, refresh));
    card.append(group);
  }
  const orphans = d.users.filter((u) => !d.clients.some((c) => c.id === u.client_id));
  if (orphans.length) {
    const group = document.createElement("div");
    group.className = "dash-group";
    group.innerHTML = `<div class="dash-group-head">No client</div>`;
    for (const u of orphans) group.append(userRow(d, u, refresh));
    card.append(group);
  }
}

/** The Model Viewer card for this login (only models from projects it can see). */
function modelViewerTick(on: boolean): { el: HTMLElement; value: () => boolean } {
  const el = document.createElement("label");
  el.className = "admin-check dash-mv";
  el.innerHTML = `<input type="checkbox" ${on ? "checked" : ""}/> Model Viewer <span class="dash-dim">(turn the 3D models of their projects, and comment)</span>`;
  return { el, value: () => el.querySelector("input")!.checked };
}

function userRow(d: Data, u: AdminUser, refresh: (i?: IssuedInvite) => void): HTMLElement {
  const row = document.createElement("div");
  row.className = `dash-user${u.active ? "" : " off"}`;
  row.innerHTML = `
    <div class="dash-user-main">
      <div class="dash-user-name">${esc(u.display_name && u.display_name !== u.username ? u.display_name : u.username)}${u.is_superuser ? ' <span class="dash-tag">Viewport</span>' : ""}${u.role === "admin" && !u.is_superuser ? ' <span class="dash-tag">client admin</span>' : ""}${u.model_viewer && !u.is_superuser ? ' <span class="dash-tag">Model Viewer</span>' : ""}${u.active ? "" : ' <span class="dash-tag warn">disabled</span>'}</div>
      <div class="dash-dim">${esc(u.username)} · ${u.last_login_at ? `last signed in ${esc(ago(u.last_login_at))}` : "never signed in"}</div>
      <div class="dash-sees">${esc(seesText(d, u.client_id, u.project_scope, u.project_ids, u.is_superuser))}</div>
    </div>
    <div class="dash-user-acts">
      ${u.is_superuser ? "" : '<button class="admin-mini" data-act="access" type="button">Access…</button>'}
      <button class="admin-mini" data-act="reset" type="button">Reset link</button>
      ${u.username === $user.get() ? "" : `<button class="admin-mini" data-act="toggle" type="button">${u.active ? "Disable" : "Enable"}</button>`}
      ${u.username === $user.get() || u.is_superuser ? "" : '<button class="admin-mini danger" data-act="delete" type="button">Delete</button>'}
    </div>`;
  row.querySelector('[data-act="delete"]')?.addEventListener("click", async () => {
    const typed = window.prompt(
      `Delete the login "${u.username}" for good?\n\n` +
        "Their comments and sign-offs stay, under this name, and the name can't be reused by anyone else. " +
        "Disable is the undo-able option.\n\nType the username to confirm:",
    );
    if (typed === null) return;
    if (typed.trim() !== u.username) {
      toast("Username didn't match - nothing deleted", "warn");
      return;
    }
    try {
      await api.deleteUser(u.id);
      toast(`${u.username} deleted`, "ok");
      refresh();
    } catch (e) {
      toast((e as Error).message, "warn", 6000);
    }
  });
  row.querySelector('[data-act="reset"]')!.addEventListener("click", async () => {
    try {
      const inv = await api.resetLink(u.id);
      refresh(inv);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast((e as Error).message, "warn", 6000);
    }
  });
  row.querySelector('[data-act="toggle"]')?.addEventListener("click", async () => {
    if (u.active && !window.confirm(`Disable ${u.username}? They are signed out at their next request.`)) return;
    try {
      await api.updateUser(u.id, { active: !u.active });
      toast(u.active ? `${u.username} disabled` : `${u.username} enabled`, "ok");
      refresh();
    } catch (e) {
      toast((e as Error).message, "warn", 6000);
    }
  });
  row.querySelector('[data-act="access"]')?.addEventListener("click", () => {
    const open = row.querySelector(".dash-edit");
    if (open) {
      open.remove();
      return;
    }
    const edit = document.createElement("div");
    edit.className = "dash-edit";
    const picker = scopePicker(d, u.client_id, u.project_scope, u.project_ids);
    const mv = modelViewerTick(u.model_viewer);
    const save = document.createElement("button");
    save.className = "tool-btn";
    save.type = "button";
    save.textContent = "Save access";
    save.addEventListener("click", async () => {
      const v = picker.value();
      if (v.project_scope === "selected" && !v.project_ids.length &&
          !window.confirm(`${u.username} will see no projects at all. Save anyway?`)) return;
      try {
        await api.updateUser(u.id, { ...v, model_viewer: mv.value() });
        toast(`${u.username}'s access saved`, "ok");
        refresh();
      } catch (e) {
        toast((e as Error).message, "warn", 6000);
      }
    });
    edit.append(picker.el, mv.el, save);
    row.append(edit);
  });
  return row;
}

// --- Invites ---
function invitesCard(card: HTMLElement, d: Data, refresh: (i?: IssuedInvite) => void): void {
  card.innerHTML = `<h2>Invites &amp; reset links</h2>`;
  if (!d.invites.length) {
    card.insertAdjacentHTML("beforeend", `<p class="dash-dim">None yet.</p>`);
    return;
  }
  const table = document.createElement("table");
  table.className = "admin-table";
  table.innerHTML = `<tr><th>For</th><th>Client</th><th>Can see</th><th>Status</th><th>Made</th><th></th></tr>`;
  for (const i of d.invites) {
    const tr = document.createElement("tr");
    const who = i.kind === "reset" ? `Reset: ${i.username ?? "?"}` : `${i.display_name ?? "(no name)"}${i.email ? ` · ${i.email}` : ""}`;
    const status = i.status === "used" ? `used${i.username ? ` → ${i.username}` : ""}` : i.status === "pending" ? `open · ${until(i.expires_at)}` : i.status;
    tr.innerHTML = `
      <td>${esc(who)}</td>
      <td>${esc(i.client_name ?? "-")}</td>
      <td>${i.kind === "reset" ? "-" : esc(seesText(d, i.client_id, i.project_scope, i.project_ids))}</td>
      <td><span class="dash-pill ${i.status}">${esc(status)}</span></td>
      <td>${esc(ago(i.created_at))}${i.created_by ? ` · ${esc(i.created_by)}` : ""}</td>
      <td>${i.status === "pending" ? '<button class="admin-mini" type="button">Withdraw</button>' : ""}</td>`;
    tr.querySelector("button")?.addEventListener("click", async () => {
      try {
        await api.revokeInvite(i.id);
        toast("Link withdrawn - it no longer works", "ok");
        refresh();
      } catch (e) {
        toast((e as Error).message, "warn", 6000);
      }
    });
    table.append(tr);
  }
  card.append(table);
}
